import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Panel, FormGroup, ControlLabel } from '@shared/components/bootstrap';

import {
  FieldFormGroup,
  MultiselectFormControl,
  SingleSelectFormControl,
  NumberFormControl,
  NestedMultiselectFormControl,
} from '@admin/components/fields';
import { RangeSlider } from '@admin/components/items/range_slider';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { client } from '@admin/libraries/apollo';
import {
  useItemCategoriesQuery,
  useMaterialCategoriesQuery,
  usePartCategoriesQuery,
  usePalletTypesQuery,
  useWarehousesQuery,
  useUsersSelectorQuery,
  usePaginatedAccountsQuery,
  ItemFiltersInput,
} from '@admin/schema';

const MAX_DIMENSION = 500;
const MIN_DIMENSION = 0;

const ITEM_STATES = [
  { id: 'packed', name: 'Packed' },
  { id: 'stored', name: 'Stored' },
  { id: 'pulled', name: 'Pulled' },
  { id: 'prepared', name: 'Prepared' },
  { id: 'enroute', name: 'En Route' },
  { id: 'returned', name: 'Returned' },
  { id: 'flagged', name: 'Flagged' },
  { id: 'lost', name: 'Lost' },
  { id: 'on_trailer', name: 'On Trailer' },
  { id: 'auctioned', name: 'Auctioned' },
  { id: 'disposed', name: 'Disposed' },
];

const INCIDENT_OPTIONS = [
  { label: 'Damaged', value: 'damaged' },
  { label: 'Damaged Before Pickup', value: 'ped' },
  { label: 'Duplicate', value: 'duplicate' },
  { label: 'Lost', value: 'lost' },
  { label: 'Auction', value: 'auction' },
  { label: 'Missing', value: 'missing' },
  { label: 'Inaccessible', value: 'inaccessible' },
];

const INCIDENT_OPTIONS_GROUPED = [
  {
    label: 'Open',
    value: 'open',
    children: INCIDENT_OPTIONS,
  },
  {
    label: 'Closed',
    value: 'closed',
    children: INCIDENT_OPTIONS,
  },
];

const GROUP_OPTIONS = [
  { label: 'In a Group', value: 'true' },
  { label: 'Not in a Group', value: 'false' },
];

type FilterProps = {
  filters: ItemFiltersInput;
  onChange(filters: ItemFiltersInput): void;
};

export const ItemsExplorerFilters: React.FC<FilterProps> = ({ filters, onChange }) => {
  const { data: itemCategoriesData } = useItemCategoriesQuery({ client });
  const { data: materialCategoriesData } = useMaterialCategoriesQuery({ client });
  const { data: partCategoriesData } = usePartCategoriesQuery({ client });
  const { data: palletTypesData } = usePalletTypesQuery({ client });
  const { data: warehousesData } = useWarehousesQuery({ client });
  const { data: usersData } = useUsersSelectorQuery({ client, variables: { filters: {} } });
  const { data: accountsData } = usePaginatedAccountsQuery({
    client,
    variables: { lostAndFound: true, page: 1 },
  });

  const itemCategories = useMemo(() => itemCategoriesData?.categories || [], [itemCategoriesData]);
  const materialCategories = useMemo(() => {
    const filtered = (materialCategoriesData?.categories || []).filter((cat) =>
      ['box', 'customer_provided_box', 'customer_box'].includes(cat.kind || ''),
    );
    return filtered.map((cat) => ({
      id: cat.id,
      name: `${cat.name}${cat.kind ? ` (${cat.kind.charAt(0).toUpperCase() + cat.kind.slice(1)})` : ''}`,
    }));
  }, [materialCategoriesData]);
  const partCategories = useMemo(() => partCategoriesData?.categories || [], [partCategoriesData]);
  const palletTypes = useMemo(() => palletTypesData?.palletTypes || [], [palletTypesData]);
  const warehouses = useMemo(() => warehousesData?.warehouses || [], [warehousesData]);
  const users = useMemo(() => usersData?.users || [], [usersData]);
  const accounts = useMemo(() => accountsData?.paginated?.results || [], [accountsData]);

  const accountOptions = useMemo(
    () =>
      accounts.map((acc: any) => {
        const accountName = acc.name || acc.customer?.name || acc.number || '';
        const regionName = acc.region?.name;
        const displayName = regionName ? `${accountName} (${regionName})` : accountName;
        return { id: acc.id, name: displayName };
      }),
    [accounts],
  );

  // Build sizing metric options based on selected categories
  // Always create a group per selected category (no deduplication)
  // to ensure selections remain distinct per category
  const sizingMetricOptions = useMemo(() => {
    if (!filters.categoryIds || filters.categoryIds.length === 0) {
      return [];
    }

    const options: Array<{ label: string; value: string; children?: Array<{ label: string; value: string }> }> = [];

    filters.categoryIds.forEach((categoryId) => {
      const category = itemCategories.find((cat) => cat.id === categoryId);
      const sizingMetric = category?.sizingMetric;

      if (sizingMetric) {
        const childOptions: Array<{ label: string; value: string }> = [];

        if (sizingMetric.range) {
          const [start, end] = sizingMetric.range.split(',').map(Number);
          for (let i = start; i <= end; i++) {
            const displayValue = sizingMetric.unit ? `${i}-${sizingMetric.unit}` : String(i);
            // Prefix child value with category ID to ensure uniqueness
            childOptions.push({ label: displayValue, value: `${categoryId}:${i}` });
          }
        } else if (sizingMetric.enumerations) {
          sizingMetric.enumerations.forEach((enumValue: string) => {
            const displayValue = sizingMetric.unit ? `${enumValue}-${sizingMetric.unit}` : enumValue;
            // Prefix child value with category ID to ensure uniqueness
            childOptions.push({ label: displayValue, value: `${categoryId}:${enumValue}` });
          });
        }

        if (childOptions.length > 0) {
          options.push({
            label: category.name,
            // Use category ID as parent value for uniqueness
            value: categoryId,
            children: childOptions,
          });
        }
      }
    });

    return options;
  }, [filters.categoryIds, itemCategories]);

  // Initialize range state from filters, or use defaults
  const [heightRange, setHeightRange] = useState({
    min: filters.minHeight ?? MIN_DIMENSION,
    max: filters.maxHeight ?? MAX_DIMENSION,
  });
  const [widthRange, setWidthRange] = useState({
    min: filters.minWidth ?? MIN_DIMENSION,
    max: filters.maxWidth ?? MAX_DIMENSION,
  });
  const [lengthRange, setLengthRange] = useState({
    min: filters.minLength ?? MIN_DIMENSION,
    max: filters.maxLength ?? MAX_DIMENSION,
  });

  // Sync range state when filters change externally (e.g., from URL params)
  useEffect(() => {
    if (filters.heightFilterType === 'range') {
      setHeightRange({
        min: filters.minHeight ?? MIN_DIMENSION,
        max: filters.maxHeight ?? MAX_DIMENSION,
      });
    }
  }, [filters.minHeight, filters.maxHeight, filters.heightFilterType]);

  useEffect(() => {
    if (filters.widthFilterType === 'range') {
      setWidthRange({
        min: filters.minWidth ?? MIN_DIMENSION,
        max: filters.maxWidth ?? MAX_DIMENSION,
      });
    }
  }, [filters.minWidth, filters.maxWidth, filters.widthFilterType]);

  useEffect(() => {
    if (filters.lengthFilterType === 'range') {
      setLengthRange({
        min: filters.minLength ?? MIN_DIMENSION,
        max: filters.maxLength ?? MAX_DIMENSION,
      });
    }
  }, [filters.minLength, filters.maxLength, filters.lengthFilterType]);

  const updateFilter = useCallback(
    (updates: Partial<ItemFiltersInput>) => {
      onChange({ ...filters, ...updates });
    },
    [filters, onChange],
  );

  const clearAllFilters = () => {
    onChange({});
  };

  return (
    <Panel className="items-search-filter-panel">
      <Panel.Header style={{ paddingTop: '12px', paddingBottom: '12px' }}>
        <Panel.Title>
          Filter Items
          <span className="pull-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={clearAllFilters}
              style={{ padding: 0, verticalAlign: 'baseline' }}
            >
              Clear All
            </button>
            <CreateTechSupportTicket ticketable={{ feature: 'ItemsExplorer', displayName: 'Items Explorer' }} />
          </span>
        </Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <FieldFormGroup label="Lost & Found Accounts:">
          <MultiselectFormControl
            placeholder="Select accounts"
            options={accountOptions}
            selectedIDs={filters.accountIds || []}
            onChange={(accountIds) => updateFilter({ accountIds })}
          />
        </FieldFormGroup>

        <FormGroup>
          <ControlLabel>Pickup Date:</ControlLabel>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="date"
              value={filters.pickupDateFrom || ''}
              onChange={(e) => updateFilter({ pickupDateFrom: e.target.value || undefined })}
              style={{ flex: 1 }}
            />
            <span>to</span>
            <input
              type="date"
              value={filters.pickupDateTill || ''}
              onChange={(e) => updateFilter({ pickupDateTill: e.target.value || undefined })}
              style={{ flex: 1 }}
            />
          </div>
        </FormGroup>

        <FormGroup>
          <ControlLabel>Categories:</ControlLabel>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ display: 'none' }}>Category Filter Type</legend>
            <div>
              <div style={{ marginBottom: '8px' }}>
                <label>
                  <input
                    type="radio"
                    name="categoryFilterType"
                    checked={(filters.categoryFilterType ?? 'any') === 'any'}
                    onChange={() => updateFilter({ categoryFilterType: 'any', categoryIds: undefined })}
                  />{' '}
                  Any{' '}
                </label>
                <label>
                  <input
                    type="radio"
                    name="categoryFilterType"
                    checked={filters.categoryFilterType === 'none'}
                    onChange={() => updateFilter({ categoryFilterType: 'none', categoryIds: undefined })}
                  />{' '}
                  Undefined{' '}
                </label>
                <label>
                  <input
                    type="radio"
                    name="categoryFilterType"
                    checked={filters.categoryFilterType === 'custom_item'}
                    onChange={() => updateFilter({ categoryFilterType: 'custom_item' })}
                  />{' '}
                  Specific{' '}
                </label>
              </div>
              {(filters.categoryFilterType ?? 'any') === 'custom_item' && (
                <>
                  <div style={{ marginBottom: '8px' }}>
                    <MultiselectFormControl
                      placeholder="Select item categories"
                      options={itemCategories}
                      selectedIDs={filters.categoryIds || []}
                      onChange={(categoryIds) => {
                        // If all categories are cleared, clear sizing selections
                        if (!categoryIds || categoryIds.length === 0) {
                          updateFilter({ categoryIds, sizingSelections: undefined });
                          return;
                        }

                        // Filter sizing selections to only keep ones valid for remaining categories
                        // Sizing selections are stored as "categoryId:selectionValue" format
                        const validSizingSelections = filters.sizingSelections
                          ? filters.sizingSelections.filter((selection) => {
                              // Extract category ID from "categoryId:selectionValue" format
                              const parts = selection.split(':');
                              if (parts.length !== 2) return false; // Invalid format, remove it

                              const [categoryId, selectionValue] = parts;

                              // Check if this category is still in the selected categories
                              return categoryIds.includes(categoryId);
                            })
                          : undefined;

                        updateFilter({
                          categoryIds,
                          sizingSelections:
                            validSizingSelections && validSizingSelections.length > 0
                              ? validSizingSelections
                              : undefined,
                        });
                      }}
                    />
                  </div>
                  {sizingMetricOptions.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <ControlLabel>Sizing Selection:</ControlLabel>
                      <NestedMultiselectFormControl
                        placeholder="Select sizing selections"
                        options={sizingMetricOptions}
                        selectedValues={
                          filters.sizingSelections
                            ? filters.sizingSelections.map((val) => {
                                // If value is already in "categoryId:selectionValue" format, use it directly
                                if (val.includes(':')) {
                                  return val;
                                }
                                // Otherwise, try to reconstruct from legacy "CategoryName:SelectionValue" format
                                // for backward compatibility
                                const parts = val.split(':');
                                if (parts.length === 2) {
                                  const [categoryName, selectionValue] = parts;
                                  const category = itemCategories.find((cat) => cat.name === categoryName);
                                  if (category) {
                                    return `${category.id}:${selectionValue}`;
                                  }
                                }
                                // Fallback: try to find matching option in current options
                                for (const parent of sizingMetricOptions) {
                                  if (parent.children) {
                                    for (const child of parent.children) {
                                      // Check if this matches the selection value (without category prefix)
                                      const childParts = child.value.split(':');
                                      if (childParts.length === 2 && childParts[1] === val) {
                                        return child.value;
                                      }
                                    }
                                  }
                                }
                                return val; // Fallback if not found
                              })
                            : []
                        }
                        onChange={(sizingSelections) => {
                          // Store the full "categoryId:selectionValue" format to avoid ambiguity
                          updateFilter({ sizingSelections });
                        }}
                      />
                    </div>
                  )}
                </>
              )}
              <div style={{ marginBottom: '8px' }}>
                <ControlLabel>Material Category:</ControlLabel>
                <MultiselectFormControl
                  className="materialCategoryMultiselect"
                  placeholder="Select material categories"
                  options={materialCategories}
                  selectedIDs={filters.materialCategoryIds || []}
                  onChange={(materialCategoryIds) => updateFilter({ materialCategoryIds })}
                />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <ControlLabel>Part Category:</ControlLabel>
                <MultiselectFormControl
                  className="partCategoryMultiselect"
                  placeholder="Select part categories"
                  options={partCategories}
                  selectedIDs={filters.partCategoryIds || []}
                  onChange={(partCategoryIds) => updateFilter({ partCategoryIds })}
                />
              </div>
            </div>
          </fieldset>
        </FormGroup>

        <FormGroup>
          <ControlLabel>Dimensions:</ControlLabel>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ display: 'none' }}>Dimension Filter Type</legend>
            <div>
              <div style={{ marginBottom: '8px' }}>
                <label>
                  <input
                    type="radio"
                    name="dimensionFilterType"
                    checked={(filters.dimensionFilterType ?? 'any') === 'any'}
                    onChange={() =>
                      updateFilter({
                        dimensionFilterType: 'any',
                        dimensionMinCuft: undefined,
                        dimensionMaxCuft: undefined,
                      })
                    }
                  />{' '}
                  Any Cuft{' '}
                </label>
                <label>
                  <input
                    type="radio"
                    name="dimensionFilterType"
                    checked={filters.dimensionFilterType === 'none'}
                    onChange={() =>
                      updateFilter({
                        dimensionFilterType: 'none',
                        dimensionMinCuft: undefined,
                        dimensionMaxCuft: undefined,
                      })
                    }
                  />{' '}
                  Undefined Cuft{' '}
                </label>
                <label>
                  <input
                    type="radio"
                    name="dimensionFilterType"
                    checked={filters.dimensionFilterType === 'custom'}
                    onChange={() => updateFilter({ dimensionFilterType: 'custom' })}
                  />{' '}
                  Cuft Range{' '}
                </label>
              </div>
              {(filters.dimensionFilterType ?? 'any') === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <NumberFormControl
                    placeholder="min cuft"
                    value={filters.dimensionMinCuft ?? undefined}
                    onChange={(dimensionMinCuft) => updateFilter({ dimensionMinCuft: dimensionMinCuft ?? undefined })}
                  />
                  <span>to</span>
                  <NumberFormControl
                    placeholder="max cuft"
                    value={filters.dimensionMaxCuft ?? undefined}
                    onChange={(dimensionMaxCuft) => updateFilter({ dimensionMaxCuft: dimensionMaxCuft ?? undefined })}
                  />
                </div>
              )}
            </div>
          </fieldset>
        </FormGroup>

        <FormGroup>
          <ControlLabel>Height (in.):</ControlLabel>
          <RangeSlider
            maxValue={MAX_DIMENSION}
            minValue={MIN_DIMENSION}
            value={heightRange}
            onChange={(value) => {
              // Update local state immediately for smooth UI while dragging
              if (filters.heightFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                setHeightRange(range);
              }
            }}
            onChangeComplete={(value) => {
              // Only update filters when user releases the slider
              if (filters.heightFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                updateFilter({
                  minHeight: range.min,
                  maxHeight: range.max,
                });
              }
            }}
            filterType={filters.heightFilterType ?? 'any'}
            filterTypeChange={(heightFilterType) => {
              if (heightFilterType === 'any') {
                updateFilter({
                  heightFilterType: 'any',
                  minHeight: undefined,
                  maxHeight: undefined,
                });
                // Reset range state to defaults
                setHeightRange({ min: MIN_DIMENSION, max: MAX_DIMENSION });
              } else {
                // When switching to range, ensure we have valid values
                const currentMin = filters.minHeight ?? heightRange.min ?? MIN_DIMENSION;
                const currentMax = filters.maxHeight ?? heightRange.max ?? MAX_DIMENSION;
                // Update state immediately to ensure InputRange receives valid values
                setHeightRange({ min: currentMin, max: currentMax });
                updateFilter({
                  heightFilterType: 'range',
                  minHeight: currentMin,
                  maxHeight: currentMax,
                });
              }
            }}
            includeUndefined={filters.includeUndefinedHeight || false}
            includeUndefinedChanged={(includeUndefinedHeight) => updateFilter({ includeUndefinedHeight })}
          />
        </FormGroup>

        <FormGroup>
          <ControlLabel>Width (in.):</ControlLabel>
          <RangeSlider
            maxValue={MAX_DIMENSION}
            minValue={MIN_DIMENSION}
            value={widthRange}
            onChange={(value) => {
              // Update local state immediately for smooth UI while dragging
              if (filters.widthFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                setWidthRange(range);
              }
            }}
            onChangeComplete={(value) => {
              // Only update filters when user releases the slider
              if (filters.widthFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                updateFilter({
                  minWidth: range.min,
                  maxWidth: range.max,
                });
              }
            }}
            filterType={filters.widthFilterType ?? 'any'}
            filterTypeChange={(widthFilterType) => {
              if (widthFilterType === 'any') {
                updateFilter({
                  widthFilterType: 'any',
                  minWidth: undefined,
                  maxWidth: undefined,
                });
                // Reset range state to defaults
                setWidthRange({ min: MIN_DIMENSION, max: MAX_DIMENSION });
              } else {
                // When switching to range, ensure we have valid values
                const currentMin = filters.minWidth ?? widthRange.min ?? MIN_DIMENSION;
                const currentMax = filters.maxWidth ?? widthRange.max ?? MAX_DIMENSION;
                // Update state immediately to ensure InputRange receives valid values
                setWidthRange({ min: currentMin, max: currentMax });
                updateFilter({
                  widthFilterType: 'range',
                  minWidth: currentMin,
                  maxWidth: currentMax,
                });
              }
            }}
            includeUndefined={filters.includeUndefinedWidth || false}
            includeUndefinedChanged={(includeUndefinedWidth) => updateFilter({ includeUndefinedWidth })}
          />
        </FormGroup>

        <FormGroup>
          <ControlLabel>Length (in.):</ControlLabel>
          <RangeSlider
            maxValue={MAX_DIMENSION}
            minValue={MIN_DIMENSION}
            value={lengthRange}
            onChange={(value) => {
              // Update local state immediately for smooth UI while dragging
              if (filters.lengthFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                setLengthRange(range);
              }
            }}
            onChangeComplete={(value) => {
              // Only update filters when user releases the slider
              if (filters.lengthFilterType === 'range') {
                const range = typeof value === 'object' ? value : { min: MIN_DIMENSION, max: MAX_DIMENSION };
                updateFilter({
                  minLength: range.min,
                  maxLength: range.max,
                });
              }
            }}
            filterType={filters.lengthFilterType ?? 'any'}
            filterTypeChange={(lengthFilterType) => {
              if (lengthFilterType === 'any') {
                updateFilter({
                  lengthFilterType: 'any',
                  minLength: undefined,
                  maxLength: undefined,
                });
                // Reset range state to defaults
                setLengthRange({ min: MIN_DIMENSION, max: MAX_DIMENSION });
              } else {
                // When switching to range, ensure we have valid values
                const currentMin = filters.minLength ?? lengthRange.min ?? MIN_DIMENSION;
                const currentMax = filters.maxLength ?? lengthRange.max ?? MAX_DIMENSION;
                // Update state immediately to ensure InputRange receives valid values
                setLengthRange({ min: currentMin, max: currentMax });
                updateFilter({
                  lengthFilterType: 'range',
                  minLength: currentMin,
                  maxLength: currentMax,
                });
              }
            }}
            includeUndefined={filters.includeUndefinedLength || false}
            includeUndefinedChanged={(includeUndefinedLength) => updateFilter({ includeUndefinedLength })}
          />
        </FormGroup>

        <FieldFormGroup label="Item State:">
          <MultiselectFormControl
            placeholder="Select item states"
            options={ITEM_STATES}
            selectedIDs={filters.itemStates || []}
            onChange={(itemStates) => updateFilter({ itemStates })}
          />
        </FieldFormGroup>

        <FieldFormGroup label="Item Creator:">
          <MultiselectFormControl
            placeholder="Select creators"
            options={users}
            selectedIDs={filters.moverIds || []}
            onChange={(moverIds) => updateFilter({ moverIds })}
          />
        </FieldFormGroup>

        <FieldFormGroup label="Warehouse:">
          <MultiselectFormControl
            placeholder="Select warehouses"
            options={warehouses}
            selectedIDs={filters.warehouseIds || []}
            onChange={(warehouseIds) => updateFilter({ warehouseIds })}
          />
        </FieldFormGroup>

        <FieldFormGroup label="Grouped:">
          <SingleSelectFormControl
            placeholder="None Selected"
            options={GROUP_OPTIONS}
            value={filters.inGroup ?? undefined}
            onChange={(inGroup: string | undefined) => updateFilter({ inGroup: inGroup || undefined })}
            isClearable={true}
          />
        </FieldFormGroup>

        <FieldFormGroup label="Incident:">
          <NestedMultiselectFormControl
            placeholder="Select incidents"
            options={INCIDENT_OPTIONS_GROUPED}
            selectedValues={
              filters.incidents
                ? [
                    ...(filters.incidents.open || []).map((kind) => `open:${kind}`),
                    ...(filters.incidents.closed || []).map((kind) => `closed:${kind}`),
                  ]
                : []
            }
            onChange={(selectedValues) => {
              const open: string[] = [];
              const closed: string[] = [];

              selectedValues.forEach((value) => {
                const [state, kind] = value.split(':');
                if (state === 'open') {
                  open.push(kind);
                } else if (state === 'closed') {
                  closed.push(kind);
                }
              });

              updateFilter({
                incidents: open.length > 0 || closed.length > 0 ? { open, closed } : undefined,
              });
            }}
          />
        </FieldFormGroup>

        <FieldFormGroup label="Pallet Type:">
          <MultiselectFormControl
            placeholder="Select pallet types"
            options={palletTypes}
            selectedIDs={filters.palletTypeIds || []}
            onChange={(palletTypeIds) => updateFilter({ palletTypeIds })}
          />
        </FieldFormGroup>
      </Panel.Body>
    </Panel>
  );
};
